import { Image, Text, View } from '@react-pdf/renderer';

import { CompTypeBadge } from '../components/CompTypeBadge.tsx';
import { Table } from '../components/Table.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { orderDetailFields, propertyDetailFields } from '../constants.ts';
import { fontStyles, propertyOrderDetailsPageStyles as styles, tableStyles } from '../styles.ts';
import { PropertyOrderProps } from '../types.ts';

export const PropertyOrderDetailsPage = ({
  subjectAddress,
  shortPropertyType,
  appraisalType,
  effectiveDate,
  inspectionDate,
  clientName,
  clientAddress,
  borrowerName,
  zoning,
  zoningCompliance,
  parcelNumber,
  propertyInterest,
  hoa,
  hoaFees,
  legalDescription,
  orderNumber,
  intendedUsers,
  intendedUse,
  propertyMapImage,
  hasArvOption,
  subjectSalesComparisonData,
}: PropertyOrderProps) => (
  <Tkv2PDFPage title="Subject Property Details" address={subjectAddress}>
    <View style={styles.section}>
      {propertyMapImage && <Image src={propertyMapImage} style={styles.propertyImage} />}
      <View style={styles.details}>
        {propertyDetailFields(
          subjectAddress,
          shortPropertyType,
          zoning,
          zoningCompliance,
          parcelNumber,
          propertyInterest,
          hoa,
          hoaFees,
          legalDescription,
        ).map(({ key, label, getValue }) => {
          const value = getValue();
          if (!value) return null;
          return (
            <View style={styles.detail} key={key}>
              <View style={styles.label}>
                <Text style={fontStyles.bodyLight}>{label}</Text>
              </View>
              <View style={styles.value}>
                <Text style={fontStyles.h4}>{value}</Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>

    {hasArvOption && (
      <>
        <Text style={fontStyles.h3}>Renovation Summary</Text>
        <Table>
          <Table.Column>
            <Table.TextCell style={{ height: 21.5 }}>Features</Table.TextCell>
            <Table.TextCell>GLA</Table.TextCell>
            <Table.TextCell>Bed Count</Table.TextCell>
            <Table.TextCell>Bath Count</Table.TextCell>
            <Table.TextCell>Basement</Table.TextCell>
            <Table.TextCell>Quality</Table.TextCell>
            <Table.TextCell>Condition</Table.TextCell>
          </Table.Column>
          <Table.Column>
            <View style={tableStyles.cell}>
              <CompTypeBadge compType="aiv" fullText compact />
            </View>
            <Table.TextCell>{subjectSalesComparisonData?.gla}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.bedCount}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.bathCount}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.basement}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.quality}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.condition}</Table.TextCell>
          </Table.Column>
          <Table.Column>
            <View style={tableStyles.cell}>
              <CompTypeBadge compType="arv" fullText compact />
            </View>
            <Table.TextCell>{subjectSalesComparisonData?.arv?.gla}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.arv?.bedCount}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.arv?.bathCount}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.arv?.basement}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.arv?.quality}</Table.TextCell>
            <Table.TextCell>{subjectSalesComparisonData?.arv?.condition}</Table.TextCell>
          </Table.Column>
        </Table>
      </>
    )}

    <View style={{ height: 20 }} />
    <Text style={fontStyles.h3}>Order Details</Text>

    <View style={styles.section}>
      <View style={styles.details}>
        {orderDetailFields(
          clientName,
          clientAddress,
          borrowerName,
          appraisalType,
          orderNumber,
          inspectionDate,
          effectiveDate,
          intendedUsers,
          intendedUse,
        ).map(({ key, label, getValue }) => {
          const value = getValue();
          if (!value) return null;
          return (
            <View style={styles.detail} key={key}>
              <View style={styles.label}>
                <Text style={fontStyles.bodyLight}>{label}</Text>
              </View>
              <View style={styles.value}>
                <Text style={fontStyles.h4}>{value}</Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  </Tkv2PDFPage>
);
